import { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trash,
  Tag,
  X,
  Sparkle,
  CircleHalf,
  CaretDown,
  ListPlus,
  Eraser,
  MinusCircle,
  MagnifyingGlass,
  Plus,
  Export,
} from '@phosphor-icons/react';
import { useContactStore } from '../../../stores/contactStore';
import { usePermission } from '../../../hooks/usePermission';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { ConfirmDialog, type ConfirmTone } from '../../../components/ui/ConfirmDialog';
import { EnrichmentCatalogModal } from './EnrichmentCatalogModal';
import { CrmExportModal } from './CrmExportModal';
import { Select } from '../../../components/ui/Select';
import {
  contactCount,
  deleteConfirmCopy,
  removeFromListConfirmCopy,
  statusChangeConfirmCopy,
  untagConfirmCopy,
} from './bulkConfirmCopy';

// Shared editorial-dark surface for floating popovers (mirrors ui/Select.tsx).
const popoverSurface =
  'bg-card border border-border/60 rounded-md shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]';
// The bar pins to the BOTTOM of the column (see the root element below), so its
// popovers open UPWARD — opening downward from a bottom-pinned bar would put
// them off-screen. The y offsets match that direction.
const popoverMotion = {
  initial: { opacity: 0, y: 4, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 4, scale: 0.98 },
  transition: { duration: 0.15, ease: [0.16, 1, 0.3, 1] as const },
};
const ctaClass =
  'inline-flex items-center justify-center shrink-0 h-9 px-3.5 text-[13px] font-medium bg-[#5e6ad2] text-white rounded-md shadow-sm hover:bg-[#7170ff] active:bg-[#5058c0] transition-colors duration-150 disabled:opacity-50';

/**
 * One confirm gate for every bulk mutation that rewrites rows the user can't
 * put back in a click. Purely additive actions (apply a tag, save to a list)
 * stay unconfirmed; delete / untag / remove-from-list / status-change all touch
 * N contacts at once and are routed through here, so the file holds exactly one
 * ConfirmDialog instead of one per action.
 */
type PendingAction = {
  kind: 'delete' | 'untag' | 'remove-from-list' | 'status';
  title: string;
  description: string;
  confirmLabel: string;
  tone: ConfirmTone;
  /**
   * Does the work and toasts its own outcome. Closes over the selection as it
   * was when the user asked — the same numbers the description quotes. Must
   * re-throw on failure: that is how `runPendingAction` knows to leave the
   * dialog open for a retry instead of silently dismissing it.
   */
  run: () => Promise<void>;
};

export function BulkActionsBar() {
  const { contacts, selectedContacts, bulkDelete, bulkTag, bulkUntag, bulkRemoveFromList, bulkUpdateStatus, deselectAllContacts, createContactList, fetchContactLists, contactLists, allTags, fetchTags, activeTag, filters } =
    useContactStore();
  const { allowed: canDeleteContacts, scope: deleteScope } = usePermission('contacts', 'hard_delete');
  // "gated": a Representative may REQUEST a deletion; the control says so.
  const deleteIsRequest = deleteScope === 'gated';
  // Analyst holds no bulk_export cell at all, so the control is inert for
  // them rather than 403-ing on click. A Rep's "gated" scope IS allowed —
  // it opens the same modal, which files a request instead of exporting.
  const canExportContacts = usePermission('contacts', 'bulk_export').allowed;
  // Tag / Save as List / Remove from Tag / Remove from List all hit
  // tags.manage or lists.manage, which an Analyst holds no cell for — hide
  // them here for the same reason the Contacts toolbar hides its Lists and
  // Tags buttons. UX-only; require_permission on the routes is the boundary.
  const canManageTags = usePermission('tags', 'manage').allowed;
  const canManageLists = usePermission('lists', 'manage').allowed;

  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [taggingBusy, setTaggingBusy] = useState(false);
  const [showRemoveTagMenu, setShowRemoveTagMenu] = useState(false);
  const [removeTagValue, setRemoveTagValue] = useState('');
  const [showRemoveListMenu, setShowRemoveListMenu] = useState(false);
  const [removeListValue, setRemoveListValue] = useState<number | ''>('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  // Enrichment kicks off a background job from within a modal that lives inside
  // this bar. Clearing the selection right away would unmount the modal, so we
  // defer the deselect until the modal is closed.
  const [enrichStarted, setEnrichStarted] = useState(false);
  const [showListInput, setShowListInput] = useState(false);
  const [listQuery, setListQuery] = useState('');
  const [savingList, setSavingList] = useState(false);
  // The single confirm gate: the action awaiting a yes, plus whether its `run`
  // is in flight (drives the dialog's spinner and blocks a second submit).
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [running, setRunning] = useState(false);
  // Holds the descriptor for the length of the dialog's exit animation. Closing
  // is `setPendingAction(null)`, and rendering the dialog straight off that
  // would blank its title mid-fade and flash a destructive red confirm button
  // back to brand indigo on the way out.
  const lastActionRef = useRef<PendingAction | null>(null);
  if (pendingAction) lastActionRef.current = pendingAction;
  const shownAction = pendingAction ?? lastActionRef.current;

  const closeAll = () => { setShowTagInput(false); setShowRemoveTagMenu(false); setShowRemoveListMenu(false); setShowStatusMenu(false); setShowListInput(false); setListQuery(''); setTagInput(''); };

  // Union of tags across the selected contacts, with how many of them carry
  // each tag — these are the only tags that make sense to remove.
  const selectedTags = useMemo(() => {
    const selected = new Set(selectedContacts);
    const counts = new Map<string, number>();
    for (const c of contacts) {
      if (!selected.has(c.id)) continue;
      for (const t of c.tags || []) counts.set(t, (counts.get(t) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [contacts, selectedContacts]);

  // Union of lists the selected contacts belong to, with how many of the
  // selection each list contains — the only lists it makes sense to remove from.
  const selectedLists = useMemo(() => {
    const selected = new Set(selectedContacts);
    return contactLists
      .map((l) => ({
        id: l.id,
        name: l.name,
        count: (l.contact_ids || []).filter((id) => selected.has(id)).length,
      }))
      .filter((l) => l.count > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contactLists, selectedContacts]);

  // Runs the pending action's `run`, owning only the dialog lifecycle: close on
  // success, stay open on failure (`run` has already toasted the reason) so the
  // user can retry without re-picking the tag/list/status they chose.
  const runPendingAction = async () => {
    const action = pendingAction;
    // ConfirmDialog's confirm button already swallows clicks while `loading`;
    // this is the belt to that braces, since `run` mutates N rows.
    if (!action || running) return;
    setRunning(true);
    try {
      await action.run();
      setPendingAction(null);
    } catch {
      /* handled in `run` — keep the dialog open */
    } finally {
      setRunning(false);
    }
  };

  const runBulkDelete = async () => {
    try {
      const result = await bulkDelete(selectedContacts);
      if (result && 'status' in result) {
        // A Representative's delete is a request for a manager to approve.
        toast.success('Sent for approval', { description: result.message });
        return;
      }
      const deleted = result?.deleted_count ?? selectedContacts.length;
      const skipped = result?.skipped ?? [];

      if (skipped.length === 0) {
        toast.success(`Deleted ${contactCount(deleted)}`);
      } else {
        // Partial or fully blocked: amber warning naming the blocking
        // campaigns so the user knows exactly what to stop or complete.
        const blockers = skipped
          .map((s) => `${s.name} (${s.campaigns.join(', ')})`)
          .join('; ');
        toast.warning(
          deleted > 0
            ? `Deleted ${deleted}, skipped ${skipped.length}`
            : 'No contacts deleted',
          {
            description:
              `Still in active campaigns: ${blockers}. ` +
              'Stop or complete those campaigns to delete these contacts.',
            duration: 8000,
          },
        );
      }
    } catch (error: any) {
      // The global API interceptor already toasts the server's detail message.
      if (!error?.response?.data?.detail) {
        toast.error('Failed to delete contacts');
      }
      throw error;
    }
  };

  const handleBulkDelete = () => {
    setPendingAction({
      kind: 'delete',
      ...deleteConfirmCopy(selectedContacts.length),
      tone: 'destructive',
      run: runBulkDelete,
    });
  };

  // Apply a tag to the selection. Mirrors saveToList: pick an existing tag from
  // the list, or type a new name and create it. bulkTag applies the same way for
  // both (a brand-new name simply becomes a new tag on those contacts).
  const applyTag = async (name: string, isNew: boolean) => {
    const finalName = name.trim();
    if (!finalName || taggingBusy) return;
    const count = selectedContacts.length;
    setTaggingBusy(true);
    try {
      await bulkTag(selectedContacts, finalName);
      toast.success(
        isNew
          ? `Created tag "${finalName}" on ${contactCount(count)}`
          : `Tagged ${contactCount(count)} with "${finalName}"`
      );
      setTagInput('');
      setShowTagInput(false);
      fetchTags();
    } catch (error) {
      toast.error('Failed to tag contacts');
    } finally {
      setTaggingBusy(false);
    }
  };

  // Pre-select the tag currently used as a filter (if the selection carries it),
  // otherwise the first available tag. Same idea for lists below.
  const openRemoveTagMenu = () => {
    const opening = !showRemoveTagMenu;
    closeAll();
    setShowRemoveTagMenu(opening);
    if (opening) {
      const preset =
        activeTag && selectedTags.some(([t]) => t === activeTag)
          ? activeTag
          : selectedTags[0]?.[0] ?? '';
      setRemoveTagValue(preset);
    }
  };

  const handleRemoveFromTag = () => {
    const tag = removeTagValue;
    if (!tag) {
      toast.error('Select a tag to remove');
      return;
    }
    const count = selectedTags.find(([t]) => t === tag)?.[1] ?? 0;
    setPendingAction({
      kind: 'untag',
      ...untagConfirmCopy(tag, count),
      tone: 'default',
      run: async () => {
        try {
          await bulkUntag(selectedContacts, tag);
          toast.success(`Removed "${tag}" from ${contactCount(count)}`);
          setShowRemoveTagMenu(false);
        } catch (error) {
          toast.error('Failed to remove tag');
          throw error;
        }
      },
    });
  };

  const openRemoveListMenu = () => {
    const opening = !showRemoveListMenu;
    closeAll();
    setShowRemoveListMenu(opening);
    if (opening) {
      fetchContactLists();
      const activeListId = filters.list_id;
      const preset =
        activeListId && selectedLists.some((l) => l.id === activeListId)
          ? activeListId
          : selectedLists[0]?.id ?? '';
      setRemoveListValue(preset);
    }
  };

  const handleRemoveFromList = () => {
    if (removeListValue === '') {
      toast.error('Select a list to remove from');
      return;
    }
    const listId = Number(removeListValue);
    const list = selectedLists.find((l) => l.id === listId);
    const count = list?.count ?? selectedContacts.length;
    const listName = list?.name ?? 'list';
    setPendingAction({
      kind: 'remove-from-list',
      ...removeFromListConfirmCopy(listName, count),
      tone: 'default',
      run: async () => {
        try {
          await bulkRemoveFromList(selectedContacts, listId);
          toast.success(`Removed ${contactCount(count)} from "${listName}"`);
          setShowRemoveListMenu(false);
        } catch (error) {
          toast.error('Failed to remove from list');
          throw error;
        }
      },
    });
  };

  const handleUpdateStatus = (status: string) => {
    const count = selectedContacts.length;
    setPendingAction({
      kind: 'status',
      ...statusChangeConfirmCopy(status, count),
      tone: 'default',
      run: async () => {
        try {
          await bulkUpdateStatus(selectedContacts, status);
          toast.success(`Updated status for ${contactCount(count)}`);
          setShowStatusMenu(false);
        } catch (error) {
          toast.error('Failed to update status');
          throw error;
        }
      },
    });
  };

  // Save the selection into a list. The backend appends to a same-named list
  // (case-insensitive) rather than duplicating, so adding to an existing list
  // is just a create call with that list's name.
  const saveToList = async (name: string, isNew: boolean) => {
    const finalName = name.trim();
    if (!finalName || savingList) return;
    setSavingList(true);
    try {
      const list = await createContactList({ name: finalName, contact_ids: selectedContacts });
      // The backend drops invalid/fake-email contacts (they can't feed
      // campaigns) and tells us how many. Only show the "(N skipped)" suffix
      // when something was actually skipped.
      const skipped = list?.skipped_invalid ?? 0;
      const added = selectedContacts.length - skipped;
      const suffix = skipped > 0 ? ` (${skipped} skipped)` : '';
      toast.success(
        isNew
          ? `Created list "${finalName}" with ${added} contacts${suffix}`
          : `Added ${added} contacts to list "${finalName}"${suffix}`
      );
      setListQuery('');
      setShowListInput(false);
      fetchContactLists();
      deselectAllContacts();
    } catch {
      // The axios interceptor already surfaces the backend error as a single
      // toast; don't add a second one here.
    } finally {
      setSavingList(false);
    }
  };

  // Inline list filter for the Save-as-List panel.
  const listQ = listQuery.trim().toLowerCase();
  const filteredLists = listQ
    ? contactLists.filter((l) => l.name.toLowerCase().includes(listQ))
    : contactLists;
  const listExactMatch = contactLists.some((l) => l.name.toLowerCase() === listQ);
  const showCreateList = listQ.length > 0 && !listExactMatch;

  // Inline tag filter for the Tag panel (mirrors the Save-as-List panel).
  const tagQ = tagInput.trim().toLowerCase();
  const filteredTags = tagQ
    ? allTags.filter((t) => t.name.toLowerCase().includes(tagQ))
    : allTags;
  const tagExactMatch = allTags.some((t) => t.name.toLowerCase() === tagQ);
  const showCreateTag = tagQ.length > 0 && !tagExactMatch;

  const btnClass =
    'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-colors duration-150 whitespace-nowrap shrink-0 text-foreground/70 hover:text-foreground hover:bg-accent dark:text-[#d0d6e0] dark:hover:bg-white/[0.06]';
  const activeClass = 'bg-accent text-foreground dark:bg-white/[0.06] dark:text-[#f7f8f8]';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.98 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        // Floating dock at the BOTTOM of the page's content column. Sticky, not
        // fixed (§27/§17): a `fixed inset-x + mx-auto` bar re-centres on the
        // viewport, so it slides sideways out from under the table whenever the
        // viewport width changes (a modal locking scroll, the scrollbar lane
        // being reclaimed) and ignores the sidebar's width entirely. Sticky
        // centres it inside the column it acts on instead.
        // `sticky bottom-4` only holds if the bar is the LAST flow child of the
        // content column — it is rendered after the table and pagination in
        // ContactsPage for exactly that reason. Moving it back above the table
        // would make this a no-op that scrolls away. The positive y offsets
        // match the direction: it rises from the bottom edge.
        // `w-fit mx-auto` keeps the dock only as wide as its own buttons rather
        // than spanning the table — every child is `shrink-0`/`whitespace-nowrap`,
        // so a full-width bar just padded empty space between Delete and Clear.
        // `max-w-full` is the floor: on a narrow column the dock stops at the
        // column edge instead of pushing the page into a horizontal scroll.
        'sticky bottom-4 z-30 w-fit max-w-full mx-auto',
        'flex items-center gap-1 rounded-xl px-3 py-2 shadow-lg',
        'bg-card border border-border/60',
        'dark:bg-[#191a1b] dark:border-white/[0.08]',
        'dark:shadow-[0_16px_40px_rgba(0,0,0,0.6),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]'
      )}
    >
      {/* Selection count */}
      <div className="flex items-center gap-2 mr-1.5 shrink-0">
        <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full bg-[#5e6ad2]/15 text-[11px] font-emphasis text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]">
          {selectedContacts.length}
        </span>
        <span className="text-[13px] font-medium text-muted-foreground whitespace-nowrap">selected</span>
      </div>

      <div className="h-5 w-px bg-border mx-1" />

      {/* Tag — searchable picker showing all existing tags, plus create-new
          (same pattern as Save as List). */}
      {canManageTags && (
      <div className="relative">
        <button
          className={cn(btnClass, showTagInput && activeClass)}
          aria-haspopup="dialog"
          aria-expanded={showTagInput}
          onClick={() => {
            const opening = !showTagInput;
            closeAll();
            setShowTagInput(opening);
            if (opening) fetchTags();
          }}
        >
          <Tag className="h-4 w-4" />
          Tag
        </button>
        <AnimatePresence>
          {showTagInput && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowTagInput(false)} />
              <motion.div {...popoverMotion} className={cn('absolute bottom-full mb-1.5 left-0 origin-bottom w-72 z-50 overflow-hidden flex flex-col', popoverSurface)}>
                <p className="px-3 pt-2.5 pb-2 text-[10px] text-muted-foreground uppercase tracking-wider shrink-0">
                  Tag {selectedContacts.length} contact{selectedContacts.length === 1 ? '' : 's'}
                </p>
                {/* Search / new-name field */}
                <div className="px-2.5 pb-2 shrink-0">
                  <div className="group flex items-center gap-2 h-9 w-full rounded-md px-2.5 bg-background border border-border/60 transition-colors duration-150 focus-within:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:focus-within:border-[#7170ff]/75">
                    <MagnifyingGlass className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" weight="bold" />
                    <input
                      autoFocus
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (showCreateTag) applyTag(tagInput, true);
                          else if (filteredTags[0]) applyTag(filteredTags[0].name, false);
                        }
                      }}
                      placeholder="Search a tag, or type a new name…"
                      aria-label="Choose or create a tag to apply"
                      className="flex-1 min-w-0 bg-transparent outline-none text-[13px] text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/60"
                      style={{ fontWeight: 510, letterSpacing: '-0.13px' }}
                    />
                  </div>
                </div>
                {/* Inline tag list — scrolls within the card */}
                <div className="max-h-52 overflow-y-auto border-t border-border/50 dark:border-white/[0.06] p-1">
                  {filteredTags.map((t) => (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => applyTag(t.name, false)}
                      disabled={taggingBusy}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-[13px] text-foreground dark:text-[#f7f8f8] hover:bg-accent/50 dark:hover:bg-white/[0.05] transition-colors duration-100 disabled:opacity-50"
                      style={{ fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <Tag className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" />
                      <span className="truncate">{t.name}</span>
                      <span className="ml-auto tabular-nums text-muted-foreground/60 dark:text-[#62666d]">{t.count}</span>
                    </button>
                  ))}
                  {showCreateTag && (
                    <button
                      type="button"
                      onClick={() => applyTag(tagInput, true)}
                      disabled={taggingBusy}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-[13px] text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-accent/50 dark:hover:bg-white/[0.05] transition-colors duration-100 disabled:opacity-50"
                      style={{ fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <Plus className="h-3.5 w-3.5 flex-shrink-0" weight="bold" />
                      <span className="truncate">Create “{tagInput.trim()}”</span>
                    </button>
                  )}
                  {filteredTags.length === 0 && !showCreateTag && (
                    <p className="px-2.5 py-2 text-[12px] text-muted-foreground/70 dark:text-[#62666d]">
                      No tags yet — type a name to create one
                    </p>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      )}

      {/* Save as List */}
      {canManageLists && (
      <div className="relative">
        <button
          className={cn(btnClass, showListInput && activeClass)}
          aria-haspopup="dialog"
          aria-expanded={showListInput}
          onClick={() => {
            const opening = !showListInput;
            closeAll();
            setShowListInput(opening);
            if (opening) fetchContactLists();
          }}
        >
          <ListPlus className="h-4 w-4" />
          Save as List
        </button>
        <AnimatePresence>
          {showListInput && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowListInput(false)} />
              <motion.div {...popoverMotion} className={cn('absolute bottom-full mb-1.5 left-0 origin-bottom w-72 z-50 overflow-hidden flex flex-col', popoverSurface)}>
                <p className="px-3 pt-2.5 pb-2 text-[10px] text-muted-foreground uppercase tracking-wider shrink-0">
                  Save {selectedContacts.length} contact{selectedContacts.length === 1 ? '' : 's'} to a list
                </p>
                {/* Search / new-name field */}
                <div className="px-2.5 pb-2 shrink-0">
                  <div className="group flex items-center gap-2 h-9 w-full rounded-md px-2.5 bg-background border border-border/60 transition-colors duration-150 focus-within:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:focus-within:border-[#7170ff]/75">
                    <MagnifyingGlass className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" weight="bold" />
                    <input
                      autoFocus
                      value={listQuery}
                      onChange={(e) => setListQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          if (showCreateList) saveToList(listQuery, true)
                          else if (filteredLists[0]) saveToList(filteredLists[0].name, false)
                        }
                      }}
                      placeholder="Search a list, or type a new name…"
                      aria-label="Choose or create a list to save to"
                      className="flex-1 min-w-0 bg-transparent outline-none text-[13px] text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/60"
                      style={{ fontWeight: 510, letterSpacing: '-0.13px' }}
                    />
                  </div>
                </div>
                {/* Inline list — scrolls within the card, so nothing floats loose */}
                <div className="max-h-52 overflow-y-auto border-t border-border/50 dark:border-white/[0.06] p-1">
                  {filteredLists.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => saveToList(l.name, false)}
                      disabled={savingList}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-[13px] text-foreground dark:text-[#f7f8f8] hover:bg-accent/50 dark:hover:bg-white/[0.05] transition-colors duration-100 disabled:opacity-50"
                      style={{ fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <ListPlus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" />
                      <span className="truncate">{l.name}</span>
                    </button>
                  ))}
                  {showCreateList && (
                    <button
                      type="button"
                      onClick={() => saveToList(listQuery, true)}
                      disabled={savingList}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-[13px] text-[#5e6ad2] dark:text-[#a5b4fc] hover:bg-accent/50 dark:hover:bg-white/[0.05] transition-colors duration-100 disabled:opacity-50"
                      style={{ fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <Plus className="h-3.5 w-3.5 flex-shrink-0" weight="bold" />
                      <span className="truncate">Create “{listQuery.trim()}”</span>
                    </button>
                  )}
                  {filteredLists.length === 0 && !showCreateList && (
                    <p className="px-2.5 py-2 text-[12px] text-muted-foreground/70 dark:text-[#62666d]">
                      No lists found
                    </p>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      )}

      {/* Enrich */}
      <button
        className={btnClass}
        onClick={() => { closeAll(); setShowEnrichModal(true); }}
      >
        <Sparkle className="h-4 w-4" />
        Enrich
      </button>

      <EnrichmentCatalogModal
        isOpen={showEnrichModal}
        onClose={() => {
          setShowEnrichModal(false);
          // If enrichment was kicked off, clear the selection now that the
          // modal is closing (matches every other bulk action).
          if (enrichStarted) {
            setEnrichStarted(false);
            deselectAllContacts();
          }
        }}
        onEnrichmentStarted={() => setEnrichStarted(true)}
        contactIds={selectedContacts}
        mode="bulk"
      />

      {/* Export to CRM */}
      <button
        className={cn(btnClass, 'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent')}
        disabled={!canExportContacts}
        title={canExportContacts ? undefined : 'Your role cannot export contacts to a CRM'}
        onClick={() => { closeAll(); setShowExportModal(true); }}
      >
        <Export className="h-4 w-4" />
        Export
      </button>

      <CrmExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        contactIds={selectedContacts}
        contactCount={selectedContacts.length}
        onExported={deselectAllContacts}
      />

      {/* Status */}
      <div className="relative">
        <button
          className={cn(btnClass, showStatusMenu && activeClass)}
          aria-haspopup="menu"
          aria-expanded={showStatusMenu}
          onClick={() => { closeAll(); setShowStatusMenu(!showStatusMenu); }}
        >
          <CircleHalf className="h-4 w-4" />
          Status
          <CaretDown className={cn('h-3 w-3 opacity-60 transition-transform duration-150', showStatusMenu && 'rotate-180')} />
        </button>
        <AnimatePresence>
          {showStatusMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowStatusMenu(false)} />
              <motion.div {...popoverMotion} className={cn('absolute bottom-full mb-1.5 left-0 origin-bottom py-1 w-40 z-50', popoverSurface)} role="menu">
                {['new', 'contacted', 'engaged', 'qualified', 'unqualified'].map((status) => (
                  <button
                    key={status}
                    role="menuitem"
                    onClick={() => handleUpdateStatus(status)}
                    className="mx-1 w-[calc(100%-0.5rem)] px-2.5 py-1.5 text-left text-[13px] rounded-md text-foreground hover:bg-accent dark:hover:bg-white/[0.06] transition-colors duration-150 capitalize"
                  >
                    {status}
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      {(canManageLists || canManageTags) && (
        <div className="h-5 w-px bg-border mx-1" />
      )}

      {/* Remove from List — pull the selection out of one list; the contacts
          themselves stay in the database (unlike Delete). */}
      {canManageLists && (
      <div className="relative">
        <button
          className={cn(btnClass, showRemoveListMenu && activeClass, 'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground/70')}
          aria-haspopup="dialog"
          aria-expanded={showRemoveListMenu}
          disabled={selectedLists.length === 0}
          title={selectedLists.length === 0 ? "The selected contacts aren't in any list" : undefined}
          onClick={openRemoveListMenu}
        >
          <MinusCircle className="h-4 w-4" />
          Remove from List
        </button>
        <AnimatePresence>
          {showRemoveListMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowRemoveListMenu(false)} />
              <motion.div {...popoverMotion} className={cn('absolute bottom-full mb-1.5 left-0 origin-bottom p-2.5 w-72 z-50', popoverSurface)}>
                {selectedLists.length === 0 ? (
                  <p className="px-1 py-1 text-[13px] text-muted-foreground">
                    The selected contacts aren't in any list.
                  </p>
                ) : (
                  <>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
                      Remove selection from a list
                    </p>
                    <div className="flex gap-2">
                      <Select
                        value={removeListValue === '' ? '' : String(removeListValue)}
                        onChange={(v) => setRemoveListValue(v === '' ? '' : Number(v))}
                        options={selectedLists.map((l) => ({ value: String(l.id), label: `${l.name} (${l.count})` }))}
                        placeholder="Select a list"
                        ariaLabel="List to remove from"
                        className="flex-1 min-w-0"
                      />
                      {/* Opens the confirm; the spinner lives on that dialog's
                          confirm button, not here. */}
                      <button
                        onClick={handleRemoveFromList}
                        disabled={pendingAction?.kind === 'remove-from-list' || removeListValue === ''}
                        className={ctaClass}
                      >
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      )}

      {/* Remove from Tag — pull one tag off every selected contact that carries it */}
      {canManageTags && (
      <div className="relative">
        <button
          className={cn(btnClass, showRemoveTagMenu && activeClass, 'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground/70')}
          aria-haspopup="dialog"
          aria-expanded={showRemoveTagMenu}
          disabled={selectedTags.length === 0}
          title={selectedTags.length === 0 ? 'The selected contacts have no tags to remove' : undefined}
          onClick={openRemoveTagMenu}
        >
          <Eraser className="h-4 w-4" />
          Remove from Tag
        </button>
        <AnimatePresence>
          {showRemoveTagMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowRemoveTagMenu(false)} />
              <motion.div {...popoverMotion} className={cn('absolute bottom-full mb-1.5 left-0 origin-bottom p-2.5 w-72 z-50', popoverSurface)}>
                {selectedTags.length === 0 ? (
                  <p className="px-1 py-1 text-[13px] text-muted-foreground">
                    The selected contacts have no tags.
                  </p>
                ) : (
                  <>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
                      Remove a tag from {selectedContacts.length} contact{selectedContacts.length === 1 ? '' : 's'}
                    </p>
                    <div className="flex gap-2">
                      <Select
                        value={removeTagValue}
                        onChange={setRemoveTagValue}
                        options={selectedTags.map(([tag, count]) => ({ value: tag, label: `${tag} (${count})` }))}
                        placeholder="Select a tag"
                        ariaLabel="Tag to remove"
                        className="flex-1 min-w-0"
                      />
                      <button
                        onClick={handleRemoveFromTag}
                        disabled={pendingAction?.kind === 'untag' || !removeTagValue}
                        className={ctaClass}
                      >
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
      )}

      <div className="h-5 w-px bg-border mx-1" />

      {/* Delete — contacts.hard_delete is Owner/Admin/Manager only (a Rep's
          sanctioned removal is Remove from List), so hide it where the API
          would 403. UX-only; the backend is the enforcement point. */}
      {canDeleteContacts && (
        <button
          className={cn(btnClass, 'text-destructive hover:text-destructive hover:bg-destructive/10 dark:text-destructive dark:hover:bg-destructive/10')}
          onClick={handleBulkDelete}
        >
          <Trash className="h-4 w-4" />
          {deleteIsRequest ? 'Request deletion' : 'Delete'}
        </button>
      )}

      {/* Was a `flex-1` spacer pushing Clear to the far edge; under `w-fit` there
          is no slack to push into, so the grouping is carried by a divider. */}
      <div className="h-5 w-px bg-border mx-1" />

      {/* Deselect */}
      <button className={cn(btnClass, 'text-muted-foreground hover:text-foreground')} onClick={deselectAllContacts}>
        <X className="h-4 w-4" />
        Clear
      </button>

      {/* The one confirm gate for delete / untag / remove-from-list / status.
          Rendered from the descriptor so the copy always names the exact count
          and target the click was made against. */}
      <ConfirmDialog
        open={pendingAction !== null}
        title={shownAction?.title ?? ''}
        description={shownAction?.description ?? ''}
        confirmLabel={shownAction?.confirmLabel ?? 'Confirm'}
        cancelLabel="Cancel"
        tone={shownAction?.tone ?? 'default'}
        loading={running}
        onConfirm={runPendingAction}
        onCancel={() => !running && setPendingAction(null)}
      />
    </motion.div>
  );
}
